import { useContext, useEffect } from 'react'
import PropTypes from 'prop-types'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons'

import { useSetState } from '@domain-group/react-hooks'

import { digObject } from '@domain-group/javascript-utils'

import {
  Box,
  Button,
  Form,
  Image,
  ModalContext,
  SidebarModal,
  SidebarNoticeBox,
  Text,
} from '@domain-group/suit-theme'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

import useUser, { useUserForm } from '@hooks/useUser'

const handleUpdateTwoFactorMethod = (selectedValue, updateTwoFactorMethod, update2FAFn) => {
  const options = {
    two_factor_method: selectedValue,
    include_user_google_authentication_token: true,
  }

  updateTwoFactorMethod({
    callbacks: {
      action: update2FAFn,
    },
    entityParams: {},
    requestOptions: options,
    toastText: 'Two Factor Method Updated',
  })
}

const defaultState = {
  twoFactorMethod: 'google_auth',
}

const Manage2FASettingsModal = (props) => {
  const { callbacks, modalKey, showModal } = props
  const {
    closeModal, updateTwoFactorMethod, updateUser,
  } = callbacks || {}

  const [state, setState] = useSetState(defaultState)
  const {
    twoFactorMethod,
  } = state

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const { user: initUser } = digObject(modalData, modalKey, {})

  const userPayload = useUser(initUser)
  const {
    callbacks: {
      updateTwoFactorMethod: update2FAFn,
      updateUser: updateFn,
    },
    updating,
    user,
    user: {
      google_authentication_token,
      otp_secret_key,
    },
  } = userPayload

  const userForm = useUserForm(user)
  const {
    entityState,
    entityState: {
      second_factor_attempts_count,
    },
    handlers,
  } = userForm

  useEffect(() => {
    const setTwoFactorMethod = google_authentication_token ? 'google_auth' : 'mobile'
    setState({ twoFactorMethod: setTwoFactorMethod })
  }, [google_authentication_token])

  const updateUserPayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions: defaultRequestOptions.user,
    toastText: 'Two Factor Authentication Settings Updated',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title="2FA" titleSecondLine="Settings" />

      <SidebarModal.Content>
        <Box flexDirection="column">

          <Form.Field direction="column" label="* 2FA Method">
            <select
              onChange={(e) => {
                handleUpdateTwoFactorMethod(e.target.value, updateTwoFactorMethod, update2FAFn)
              }}
              value={twoFactorMethod}
            >
              <option value="mobile">Mobile</option>
              <option value="google_auth">Google Authenticator</option>
            </select>
          </Form.Field>

          {google_authentication_token && (
            <>
              <SidebarNoticeBox boxProps={{ marginTop: 'large' }}>
                <SidebarNoticeBox.Title>
                  <FontAwesomeIcon color="orange" icon={faExclamationTriangle} />
                  <Text color="orange" style={{ display: 'inline-block' }}>&nbsp;Rescan the code</Text>
                </SidebarNoticeBox.Title>

                <SidebarNoticeBox.Paragraph>
                  You have changed the 2FA method. You will need to rescan the code.
                </SidebarNoticeBox.Paragraph>
              </SidebarNoticeBox>

              <Form.Field label="QR Code">
                <Box justifyContent="center">
                  <Image
                    borderRadius={5}
                    boxProps={{ border: '1px solid', borderColor: 'lineColor' }}
                    height={180}
                    url={
                      `https://chart.googleapis.com/chart?chs=180x180&chld=M|0&cht=qr&chl=${google_authentication_token}`
                    }
                    width={180}
                  />
                </Box>
                <Text marginTop="medium" variant="ellipsis">
                  {otp_secret_key}
                </Text>
              </Form.Field>
            </>
          )}

          <Form.Field label="* 2FA Attempts" marginTop="large">
            <input
              name="second_factor_attempts_count"
              type="number"
              value={second_factor_attempts_count}
              {...handlers}
            />
          </Form.Field>

        </Box>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          loading={updating}
          onClick={() => updateUser(updateUserPayload)}
          size="large"
        >
          Save 2FA Settings
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

Manage2FASettingsModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

Manage2FASettingsModal.defaultProps = {
  modalKey: 'Manage2FASettingsModal',
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <Manage2FASettingsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
